import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LoadingButton } from "@mui/lab";
import { useTour } from "@reactour/tour";
import PermitPackageIndicators from "../indicators/PermitPackageIndicators";
import React, { useEffect, useState, useMemo } from "react";
import {
  handleDownloadInstructions,
  setModalsState,
  setPermitLoadings,
} from "../../../store/actions/uiActions/myPermitsActions";
import { getPermitTypeData } from "../../permitForms/permitFormHelpers";

const PermitPackageMiddleContent = ({
  currentLeftNotarized,
  currentLeftInProgress,
  setFileSizeModalOpen,
  selectedPermitType,
  isMobile = false,
}) => {
  const noc_data = useSelector((state) => state.nocData.nocData);
  const nocCreatingStatus = useSelector((state) => state.nocData.pdfProgress);
  const { activeTab, selectedRow, leftValue, instructionDownloading } =
    useSelector((state) => state.myPermitsComponent);
  const { cancelPermitLoading, createLoading, downloading, tempLoading } =
    useSelector((state) => state.myPermitsComponent.permits.loadings);

  const { isOpen } = useTour();

  const dispatch = useDispatch();

  const [currentNocStatus, setCurrentNocStatus] = useState(null);

  const currentPermitData = useMemo(
    () => getPermitTypeData(selectedRow, selectedPermitType),
    [selectedRow, selectedPermitType],
  );

  useEffect(() => {
    if (selectedRow && nocCreatingStatus && nocCreatingStatus.length > 0) {
      const foundStatus = nocCreatingStatus.find(
        (item) => item.id === selectedRow?.task_id,
      );

      if (foundStatus) {
        setCurrentNocStatus(foundStatus);
      } else {
        setCurrentNocStatus(null);
      }
    }
  }, [nocCreatingStatus, selectedRow]);

  return (
    <>
      <Box
        className="step15"
        sx={{
          borderLeft: isMobile ? "none" : "4px solid #d3d3d3",
          ml: isMobile ? 0 : 1,
          flex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box sx={{ mt: 1, flexShrink: 0 }}>
          <Typography
            variant="h5"
            align="center"
            color="primary"
            px={2}
            mb={0}
            sx={{ fontSize: "22px" }}
          >
            {currentPermitData?.selected_documents?.[leftValue]?.label}
          </Typography>

          <Typography
            variant="body2"
            fontWeight="bold"
            align="center"
            color="primary"
            px={2}
            mb={0}
          >
            {selectedRow?.city
              ? selectedRow?.city
                  .replace(/_/g, " ")
                  .split(" ")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")
              : "County Specific"}{" "}
            - {selectedRow?.county}
          </Typography>
        </Box>
        {/* Status Indicators */}

        <Box sx={{ flexShrink: 0 }}>
          <PermitPackageIndicators />
        </Box>

        {/* END OF STATUS INDICATORS */}

        {/* MIDDLE CONTENT */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {activeTab === 1 && selectedRow?.assigned_package?.is_assigned ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
              }}
            >
              <Typography
                pt={5}
                sx={{ px: 3 }}
                variant="h5"
                align="center"
                color="warning"
              >
                Permit Package has been assigned to the contractor!
              </Typography>
            </Box>
          ) : tempLoading ||
            selectedRow?.documents?.[
              selectedRow?.selected_documents?.[leftValue]?.name
            ]?.status === "In Progress" ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h5"
                align="center"
                color="warning"
                sx={{ fontSize: "22px" }}
                pt={2}
                px={1}
              >
                Please wait while the document is being created
              </Typography>
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img src="/img/gif/HI9M.gif" alt="loading" width={100} />
              </Box>
            </Box>
          ) : currentNocStatus && !currentNocStatus?.queue_data?.done ? (
            //? IN QUEUE //////////////////////////////////////////
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                color="warning"
                sx={{ fontSize: "22px" }}
                pt={1}
                px={2}
              >
                {currentNocStatus?.queue_data?.in_processing_window === false
                  ? "Your Notice of Commencement is currently in queue position:"
                  : "Please wait while the document is being created"}
              </Typography>
              {currentNocStatus?.queue_data?.in_processing_window === false && (
                <Typography
                  variant="h4"
                  align="center"
                  color="primary"
                  pt={1}
                  px={2}
                >
                  {currentNocStatus?.queue_data?.position}
                </Typography>
              )}
              <Box sx={{ textAlign: "center", mt: 1 }}>
                <img src="/img/gif/HI9M.gif" alt="loading" width={80} />
              </Box>
            </Box>
          ) : //? ///////////////////////////////////////////////////////////
          noc_data?.failed_counties.includes(selectedRow?.county) &&
            selectedRow?.documents?.noc?.is_created === false &&
            selectedRow?.documents?.noc?.status === "failed" ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                color="error"
                pt={2}
                px={2}
              >
                {selectedRow?.county} Appraiser's website is temporarily
                unavailable. Once the website can be accessed, the NOC will be
                automatically generated.
              </Typography>
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img src="/img/gif/HI9M.gif" alt="loading" width={80} />
              </Box>
            </Box>
          ) : selectedRow?.documents?.[
              selectedRow?.selected_documents?.[leftValue]?.name
            ]?.status === "failed" ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                pt={2}
                pb={2}
                px={1}
                color="error"
              >
                Based on the address you provided, we were unable to locate the
                property
              </Typography>
              <Button
                onClick={() =>
                  dispatch(setModalsState({ permitDrawerOpen: true }))
                }
                sx={{ mt: 1, minWidth: "80%" }}
                variant="contained"
                color="warning"
                endIcon={<EditIcon />}
              >
                UPDATE DETAILS
              </Button>
            </Box>
          ) : selectedRow?.documents?.[
              selectedRow?.selected_documents?.[leftValue]?.name
            ]?.status === "more" ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                pt={2}
                pb={2}
                px={1}
                color="error"
              >
                There are more than one property addresses found for the given
                address
              </Typography>
              <Button
                onClick={() =>
                  dispatch(setModalsState({ permitDrawerOpen: true }))
                }
                sx={{ mt: 1, minWidth: "80%" }}
                variant="contained"
                color="warning"
                endIcon={<EditIcon />}
              >
                UPDATE DETAILS
              </Button>
            </Box>
          ) : !currentPermitData?.selected_documents?.[leftValue]?.created ? (
            <Box
              sx={{
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                color="warning"
                sx={{ fontSize: "18px" }}
                pt={2}
                px={2}
                pb={1}
              >
                The document has not yet been created
              </Typography>
              <LoadingButton
                loading={createLoading}
                disabled={cancelPermitLoading}
                onClick={async () => {
                  if (!isOpen) {
                    dispatch(setPermitLoadings({ createLoading: true }));
                    await dispatch(setModalsState({ permitDrawerOpen: true }));
                  } else {
                    return;
                  }
                }}
                sx={{ mt: 0, minWidth: "80%" }}
                variant="contained"
                color="success"
                size="small"
              >
                CREATE FORM
              </LoadingButton>
            </Box>
          ) : (
            currentPermitData?.selected_documents?.[leftValue]?.created && (
              <>
                <Box
                  sx={{
                    mx: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {currentLeftNotarized && currentLeftInProgress && (
                    <>
                      <Typography
                        px={2}
                        pt={2}
                        mb={2}
                        align="center"
                        variant="h5"
                        sx={{
                          color: "#4caf50",
                          fontSize: "22px",
                        }}
                      >
                        Notarization has been initiated and is currently in
                        progress...
                      </Typography>
                      <Typography
                        px={2}
                        mb={0}
                        align="center"
                        variant="body1"
                        color="primary"
                      ></Typography>
                    </>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 1,
                      mt: 4,
                      px: 1,
                    }}
                  >
                    {!currentLeftNotarized && (
                      <Button
                        disabled={
                          Boolean(instructionDownloading) ||
                          Boolean(downloading) ||
                          currentLeftInProgress
                        }
                        onClick={() => {
                          dispatch(setModalsState({ permitDrawerOpen: true }));
                        }}
                        sx={{ mt: 1, minWidth: "10rem", px: 0.8 }}
                        variant="contained"
                        color="warning"
                        endIcon={<EditIcon />}
                        size="small"
                      >
                        Update Document
                      </Button>
                    )}

                    <LoadingButton
                      disabled={
                        Boolean(instructionDownloading) || currentLeftInProgress
                      }
                      loading={
                        downloading ===
                        currentPermitData?.selected_documents?.[leftValue]
                          ?.name +
                          selectedRow?.task_id
                      }
                      onClick={() => {
                        setFileSizeModalOpen(true);
                      }}
                      sx={{ mt: 1, minWidth: "10rem", px: 0.8 }}
                      variant="contained"
                      color={currentLeftNotarized ? "success" : "primary"}
                      endIcon={<DownloadIcon />}
                      size="small"
                    >
                      {currentLeftNotarized
                        ? "Download Notarized Form"
                        : "Download Form"}
                    </LoadingButton>
                  </Box>
                  {["pais", "pass"].includes(
                    currentPermitData?.selected_documents?.[leftValue]?.name,
                  ) &&
                    currentPermitData?.selected_documents?.[leftValue]
                      ?.created && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0,
                          mt: 0,
                        }}
                      >
                        <LoadingButton
                          disabled={Boolean(downloading)}
                          loading={
                            instructionDownloading ===
                            currentPermitData?.selected_documents?.[leftValue]
                              ?.name +
                              "inst" +
                              selectedRow?.task_id
                          }
                          onClick={() => dispatch(handleDownloadInstructions())}
                          sx={{ mt: 1, minWidth: "10rem" }}
                          color="primary"
                          endIcon={<UploadFileIcon />}
                          size="small"
                        >
                          Download Instructions
                        </LoadingButton>
                      </Box>
                    )}
                </Box>
              </>
            )
          )}
        </Box>
      </Box>
    </>
  );
};

export default React.memo(PermitPackageMiddleContent);
